import { useState, type RefObject } from "react";
import moment from "moment";
import { Link, useLocation, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { snakeCaseToSentenceCase } from "../../../utils/formatText";
import { formatCurrency, formatNumber } from "../../../utils/formatNumber";
import logo from "../../../assets/images/logo/Trellis_Logomark_color.png";
import { useDates } from "../../../dates/DatesContext";
import { useAppSelector } from "../../../redux/hooks";
import { CustomTriggerTooltip, OverlayArrow } from "../../core/basic/Tooltip";
import { Button } from "../../core/basic/Button";
import { TextAreaField } from "../../fields/base/TextAreaField";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../../zodios/client";
import type { Annotation } from "../../../zodios/apiDefinitions/api/annotations";
import { errorToast, successToast } from "../../../utils/toasts";
import { linkOptionsFromHref } from "../../../utils/searchParams";
import { css, cx } from "../../../../styled-system/css";
import { buttonRecipe } from "../../core/basic/Button";
import { HStack, VStack, Box, styled } from "../../../../styled-system/jsx";
import { FaXmark, FaPencil, FaTrash, FaRotateLeft } from "react-icons/fa6";
import { Dialog, GridList, GridListItem, Heading, Popover } from "react-aria-components";
import { popoverStyles } from "../../fields/styles/styles";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import { getNTMStatusToggleLabel } from "./getNTMStatusToggleLabel";

export const supportedOrgLevelAnnotationPaths = [
  "/user/advertising/dashboard",
  "/user/merchandising/category",
  "/user/dashboard",
  "/user/report/product",
  "/user/report/target",
  "/user/report/advertisement",
];

// Panda CSS style definitions
const popoverStyle = cx(
  popoverStyles,
  css({
    width: "300px",
    maxWidth: "calc(100vw - 24px)",
  }),
);

// When the popover is short on room only the annotation list shrinks (everything else sets `flexShrink: 0`), so it
// stays the one thing that scrolls. The dialog scrolls only once the header, the form and a row of the list don't fit.
const dialogStyle = css({
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  padding: "medium",
  outline: "none",
  overflowY: "auto",
});

const labelBodyStyle = css({
  maxHeight: "300px",
  marginRight: "-10px",
  paddingRight: "4px",
  scrollbarColor: "{colors.lightgrey} transparent",
  scrollbarWidth: "thin",
  overflowX: "hidden",
  overflowY: "auto",
  // Every annotation is at least as tall as its avatar, so this keeps one in view without padding a short list
  "&:not([data-empty])": {
    minHeight: "40px",
  },
});

const labelHeaderStyle = css({
  display: "flex",
  flexShrink: 0,
  position: "relative",
  justifyContent: "space-between",
  alignItems: "center",
  borderBottom: "1px solid {colors.border.default}",
  marginBottom: "medium",
  paddingBottom: "small",
});

const labelTitleStyle = css({
  flex: 1,
  margin: 0,
  color: "text.heading",
  fontSize: "medium",
  fontWeight: 700,
  letterSpacing: "0.8px",
  textTransform: "uppercase",
});

const closeLabelButtonStyle = css({
  position: "relative",
  top: "0",
  right: "0",
  width: "20px",
  height: "20px",
  color: "grey",
  fontSize: "medium",
  _hover: {
    color: "text.primary",
  },
});

const AnnotationCard = styled(GridListItem, {
  base: {
    all: "unset",
    display: "grid",
    alignItems: "center",
    gridTemplateColumns: "auto minmax(0, 1fr) auto",
    gridTemplateRows: "auto auto",
    rowGap: "xsmall",
    columnGap: "small",
    borderBottom: "1px solid {colors.border.default}",
    paddingY: "small",
    paddingX: 0,
    overflow: "visible",
    _first: {
      paddingTop: 0,
    },
    _last: {
      borderBottom: "none",
      paddingBottom: 0,
    },
    "&[data-focus-visible='true']": {
      outline: "2px solid {colors.trellisPalette.3}",
      outlineOffset: "2px",
    },
  },
});

const annotationItemImageStyle = css({
  objectFit: "cover",
  borderRadius: "light",
  width: "40px",
  height: "40px",
});

const inlineDateStyle = css({
  width: "100%",
  marginBottom: "xsmall",
  color: "text.secondary",
  fontSize: "small",
  fontWeight: 600,
});

const confirmMessageStyle = css({
  padding: "large 0",
  color: "trellisPalette.0",
  fontSize: "large",
  lineHeight: "1.5rem",
  textAlign: "center",
});

const annotationDescriptionStyle = css({
  maxWidth: "180px",
  margin: 0,
  color: "text.secondary",
  fontSize: "small",
  fontWeight: 400,
  lineHeight: "1.6",
});

const annotationLowerStyle = css({
  display: "flex",
  gap: "small",
  alignItems: "center",
  gridColumn: "1 / -1",
  marginTop: "xsmall",
  paddingTop: "small",
});

const annotationFormStyle = css({
  display: "flex",
  flexShrink: 0,
  position: "relative",
  flexDirection: "column",
  borderTop: "1px solid {colors.border.default}",
  marginTop: "medium",
  paddingTop: "medium",
  textAlign: "center",
});

const createdByInitialsStyle = css({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "pill",
  width: "40px",
  minWidth: "40px",
  height: "40px",
  color: "white",
  fontSize: "large",
  fontWeight: 700,
  textTransform: "uppercase",
  background: "trellisPalette.6",
});

const trellisLogoStyle = css({
  width: "25px",
  height: "25px",
});

const impactDataStyle = css({
  color: "text.secondary",
  fontSize: "small",
  lineHeight: "1.6",
});

// Helper function to format meta details for annotations
const formatMetaDetails = (
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  t: TFunction<any, any>,
  meta: Exclude<Annotation["meta"], undefined>,
  marketplace: string,
) => {
  return Object.keys(meta).map((m, i) => {
    const metaItem = meta[m]!;
    const metric = snakeCaseToSentenceCase(m);

    const ntmStatusLabel = getNTMStatusToggleLabel(t, m, metaItem.current);
    if (ntmStatusLabel) {
      return (
        <li className={impactDataStyle} key={i}>
          {ntmStatusLabel}
        </li>
      );
    }

    // Handle boolean values
    if (typeof metaItem.change === "boolean") {
      return (
        <li className={impactDataStyle} key={i}>
          {t(($) => $.annotations["{metric} changed from {previous} to {current}"], {
            metric,
            previous: String(metaItem.previous),
            current: String(metaItem.current),
          })}
        </li>
      );
    }

    // Handle number values
    const percentSuffix = m === "max_acos" ? "%" : "";
    const formatValue = (v: number) =>
      m === "daily_budget" || m === "product_price" ?
        formatCurrency(Math.abs(v), {
          marketPlace: marketplace,
        })
      : formatNumber(Math.abs(v), { decimalPlaces: 2 });
    // In the non-boolean branch above, `change` narrows to number; `previous`/`current` form a
    // triplet with it at the source, so they are numbers here too.
    const amount = `${formatValue(metaItem.change)}${percentSuffix}`;
    const previousFormatted = `${formatValue(metaItem.previous as number)}${percentSuffix}`;
    const currentFormatted = `${formatValue(metaItem.current as number)}${percentSuffix}`;

    return (
      <li className={impactDataStyle} key={i}>
        {metaItem.change > 0 ?
          t(($) => $.annotations["{metric} increased by {amount} from {previous} to {current}"], {
            metric,
            amount,
            previous: previousFormatted,
            current: currentFormatted,
          })
        : t(($) => $.annotations["{metric} decreased by {amount} from {previous} to {current}"], {
            metric,
            amount,
            previous: previousFormatted,
            current: currentFormatted,
          })
        }
      </li>
    );
  });
};

interface AnnotationPanelProps {
  date: string;
  onClose: () => void;
  selectedProduct: unknown;
  data?: { annotations?: Array<Annotation> };
  marketplace: string;
  category: string | number | null;
  advertisement: string | number | null;
}

// Helper function to get annotation configuration
interface AnnotationConfig {
  linkPath: string;
  displayName: string;
  actionText: string;
  showMeta: boolean;
}

const getAnnotationConfig = (
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  t: TFunction<any, any>,
  annotation: Annotation,
): AnnotationConfig | null => {
  switch (annotation.annotation_type) {
    case "AD_CREATED":
      return {
        linkPath: `/user/advertising/dashboard/ads/${annotation.advertisement?.id}`,
        displayName: annotation.advertisement?.name || t(($) => $.annotations["Ad"]),
        actionText: t(($) => $.annotations["Created"]),
        showMeta: false,
      };
    case "AD_UPDATED":
      return {
        linkPath: `/user/advertising/dashboard/ads/${annotation.advertisement?.id}`,
        displayName: annotation.advertisement?.name || t(($) => $.annotations["Ad"]),
        actionText: t(($) => $.annotations["Updated"]),
        showMeta: true,
      };
    case "PRODUCT_PRICE_CHANGE":
      return {
        linkPath: `/user/merchandising/product/${annotation.product?.id}`,
        displayName: annotation.product?.asin || t(($) => $.annotations["Product"]),
        actionText: t(($) => $.annotations["Updated"]),
        showMeta: true,
      };
    case "PRODUCT_CONTENT_CHANGE":
      return {
        linkPath: `/user/content/seo?id=${annotation.product?.id}`,
        displayName: `${annotation.product?.asin} (${annotation.product?.sku})`,
        actionText: t(($) => $.annotations["Content Updated"]),
        showMeta: false,
      };
    case "PROMO_STARTED":
      return {
        linkPath: `/user/merchandising/product/${annotation.product?.id}`,
        displayName: annotation.product?.asin || t(($) => $.annotations["Product"]),
        actionText: t(($) => $.annotations["Promotion Started"]),
        showMeta: false,
      };
    case "PROMO_ENDED":
      return {
        linkPath: `/user/merchandising/product/${annotation.product?.id}`,
        displayName: annotation.product?.asin || t(($) => $.annotations["Product"]),
        actionText: t(($) => $.annotations["Promotion Ended"]),
        showMeta: false,
      };
    case "CUSTOM":
      if (annotation.product) {
        return {
          linkPath: `/user/merchandising/product/${annotation.product.id}`,
          displayName: annotation.product.asin,
          actionText: annotation.description || "",
          showMeta: false,
        };
      }
      if (annotation.advertisement) {
        return {
          linkPath: `/user/advertising/dashboard/ads/${annotation.advertisement.id}`,
          displayName: annotation.advertisement.name,
          actionText: annotation.description || "",
          showMeta: false,
        };
      }
      return null;
    default:
      return null;
  }
};

// Helper function to get text value for accessibility
const getAnnotationTextValue = (
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  t: TFunction<any, any>,
  annotation: Annotation,
): string => {
  const config = getAnnotationConfig(t, annotation);
  if (config) {
    return `${config.displayName} ${config.actionText}`;
  }
  return annotation.description || t(($) => $.annotations["Annotation"]);
};

// Component for delete confirmation dialog
interface AnnotationDeleteConfirmationProps {
  annotation: Annotation;
  date: string;
  group: string;
  marketplace: string;
  onCancel: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}

const AnnotationDeleteConfirmation = ({
  annotation,
  date,
  group,
  marketplace,
  onCancel,
  onConfirm,
  isDeleting,
}: AnnotationDeleteConfirmationProps) => {
  const { t } = useTranslation(["annotations"]);
  const fmtLong = useDateFormatter("long");
  const fmtMonthYear = useDateFormatter("monthYear");
  const config = getAnnotationConfig(t, annotation);
  const showMeta = config?.showMeta && annotation.meta;

  return (
    <>
      <div className={labelHeaderStyle}>
        <Heading slot="title" level={6} className={labelTitleStyle}>
          {moment(date, ["MM-DD-YYYY", "MMM-YY"]).format("MMMM DD, YYYY") === "Invalid date" ?
            date
          : group === "month" ?
            fmtMonthYear.format(moment(date, ["YYYY-MM-DD"]).toDate())
          : fmtLong.format(moment(date, ["YYYY-MM-DD", "MMM-DD-YYYY", "MMM-DD"]).toDate())}
        </Heading>
        <Button variant="contentOnly" onPress={onCancel} className={closeLabelButtonStyle}>
          <FaXmark />
        </Button>
      </div>
      <VStack
        className={css({ flexShrink: 0, minHeight: "200px", overflowX: "hidden" })}
        gap="medium"
        justifyContent="center"
        alignItems="center"
        paddingY="xlarge"
      >
        <p className={confirmMessageStyle}>
          {t(($) => $.annotations["Are you sure you want to permanently delete this annotation?"])}
        </p>

        <Box
          className={css({
            border: "1px solid token(colors.grey)",
            backgroundColor: "white",
          })}
          borderRadius="standard"
          width="100%"
          padding="medium"
        >
          <div
            className={css({
              display: "grid",
              alignItems: "center",
              gridTemplateColumns: "auto 1fr",
              gridTemplateRows: "auto auto",
              rowGap: "xsmall",
              columnGap: "small",
            })}
          >
            {/* Avatar */}
            <CustomTriggerTooltip
              toolTip={
                annotation.created_by?.first_name ?
                  `${annotation.created_by.first_name} ${annotation.created_by.last_name} ${
                    annotation.created_by.is_staff ? t(($) => $.annotations["(Trellis Staff)"]) : ""
                  }`
                : t(($) => $.annotations["Automatically generated by Trellis"])
              }
              placement={"top"}
            >
              <span className={createdByInitialsStyle}>
                {annotation.created_by?.first_name ?
                  annotation.created_by.first_name.split("")[0]
                : <img className={trellisLogoStyle} src={logo} alt={t(($) => $.annotations["Trellis logo"])} />}
              </span>
            </CustomTriggerTooltip>

            {/* Content */}
            <VStack gap="xsmall" alignItems="flex-start" width="100%">
              {group !== "day" && (
                <span className={inlineDateStyle}>
                  {fmtLong.format(moment(annotation.ts.slice(0, 10), "YYYY-MM-DD").toDate())}
                </span>
              )}
              <p className={annotationDescriptionStyle}>
                <AnnotationContent annotation={annotation} />
              </p>
            </VStack>

            {/* Meta details */}
            {showMeta && (
              <ul
                className={css({
                  gridColumn: "1 / -1",
                  margin: 0,
                  paddingLeft: "20px",
                })}
              >
                {formatMetaDetails(t, annotation.meta ?? {}, marketplace)}
              </ul>
            )}
          </div>
        </Box>

        <HStack gap="medium" justifyContent="center" width="100%">
          <Button variant="secondary" onPress={onCancel}>
            {t(($) => $.annotations["Cancel"])}
          </Button>
          <Button variant="primary" onPress={onConfirm} isDisabled={isDeleting}>
            {isDeleting ? t(($) => $.annotations["Deleting..."]) : t(($) => $.annotations["Delete"])}
          </Button>
        </HStack>
      </VStack>
    </>
  );
};

// Component to render annotation content
interface AnnotationContentProps {
  annotation: Annotation;
}

const AnnotationContent = ({ annotation }: AnnotationContentProps) => {
  const { t } = useTranslation(["annotations"]);
  const config = getAnnotationConfig(t, annotation);

  if (!config) {
    return annotation.description;
  }

  return (
    <>
      <CustomTriggerTooltip toolTip={config.displayName} placement={"top"}>
        <Link
          className={cx(
            buttonRecipe({ variant: "link" }),
            css({
              display: "inline-block",
              maxWidth: "100%",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }),
          )}
          {...linkOptionsFromHref(config.linkPath)}
        >
          {config.displayName}
        </Link>
      </CustomTriggerTooltip>
      {config.actionText}
    </>
  );
};

// Component to render individual annotation item with avatar, content, and actions
interface AnnotationItemProps {
  annotation: Annotation;
  group: string;
  marketplace: string;
  selectedProduct: unknown;
  setSelectedAnnotation: (annotation: Annotation | null) => void;
  setDeletingAnnotationId: (id: number | null) => void;
  deleteAnnotationIsPending: boolean;
}

const AnnotationItem = ({
  annotation,
  group,
  marketplace,
  selectedProduct,
  setSelectedAnnotation,
  setDeletingAnnotationId,
  deleteAnnotationIsPending,
}: AnnotationItemProps) => {
  const { t } = useTranslation(["annotations"]);
  const fmtLong = useDateFormatter("long");
  const config = getAnnotationConfig(t, annotation);
  const showMeta = config?.showMeta && annotation.meta;

  return (
    <>
      {/* Row 1, Col 1: Avatar */}
      <CustomTriggerTooltip
        toolTip={
          annotation.created_by?.first_name ?
            `${annotation.created_by.first_name} ${annotation.created_by.last_name} ${
              annotation.created_by.is_staff ? t(($) => $.annotations["(Trellis Staff)"]) : ""
            }`
          : t(($) => $.annotations["Automatically generated by Trellis"])
        }
        placement={"top"}
      >
        <span className={createdByInitialsStyle}>
          {annotation.created_by?.first_name ?
            annotation.created_by.first_name.split("")[0]
          : <img className={trellisLogoStyle} src={logo} alt={t(($) => $.annotations["Trellis logo"])} />}
        </span>
      </CustomTriggerTooltip>

      {/* Row 1, Col 2: Content (date + link/description) */}
      <VStack gap="xsmall" alignItems="flex-start" width="100%">
        {group !== "day" && (
          <span className={inlineDateStyle}>
            {fmtLong.format(moment(annotation.ts.slice(0, 10), "YYYY-MM-DD").toDate())}
          </span>
        )}
        <p className={annotationDescriptionStyle}>
          <AnnotationContent annotation={annotation} />
        </p>
      </VStack>

      {/* Row 1, Col 3: Actions */}
      {group === "day" && (
        <HStack
          className={css({
            visibility: "hidden",
            "[data-hovered] &": {
              visibility: "visible",
            },
          })}
          gap="xsmall"
          justifyContent="flex-end"
          alignItems="center"
        >
          {annotation.annotation_type === "CUSTOM" && (
            <Button
              variant="contentOnly"
              onPress={() => {
                setSelectedAnnotation(annotation);
              }}
              className={css({
                minWidth: "32px",
                minHeight: "32px",
                padding: "small",
                fontSize: "large",
              })}
            >
              <FaPencil />
            </Button>
          )}
          <Button
            variant="contentOnly"
            onPress={() => {
              setDeletingAnnotationId(annotation.id);
            }}
            isDisabled={deleteAnnotationIsPending}
            className={css({
              minWidth: "32px",
              minHeight: "32px",
              padding: "small",
              fontSize: "large",
            })}
          >
            <FaTrash />
          </Button>
        </HStack>
      )}

      {/* Row 2: Meta details spanning all columns */}
      {showMeta && (
        <ul
          className={css({
            gridColumn: "1 / -1",
            maxWidth: "250px",
            margin: 0,
            paddingLeft: "20px",
          })}
        >
          {formatMetaDetails(t, annotation.meta ?? {}, marketplace)}
        </ul>
      )}

      {!selectedProduct && annotation.product?.product_image_url && (
        <div className={annotationLowerStyle}>
          <img
            src={annotation.product.product_image_url}
            alt={annotation.product.product_title}
            className={annotationItemImageStyle}
          />
          {annotation.annotation_type !== "CUSTOM" && (
            <Link
              className={buttonRecipe({ variant: "link" })}
              key={"productLink" + annotation.product.id}
              to="/user/merchandising/product/$product"
              params={{ product: String(annotation.product.id) }}
            >
              {annotation.product.asin}
            </Link>
          )}
        </div>
      )}
    </>
  );
};

const AnnotationPanel = ({
  date,
  onClose,
  selectedProduct,
  data,
  marketplace,
  category,
  advertisement,
}: AnnotationPanelProps) => {
  const { t } = useTranslation(["annotations"]);
  const fmtLong = useDateFormatter("long");
  const fmtMonthYear = useDateFormatter("monthYear");
  const user = useAppSelector((state) => state.user);
  const [newAnnotation, setNewAnnotation] = useState({
    annotation: "",
    createdBy: user.user_full_name,
    date,
  });
  const [selectedAnnotation, setSelectedAnnotation] = useState<Annotation | null>(null);
  const [deletingAnnotationId, setDeletingAnnotationId] = useState<number | null>(null);

  const { group } = useDates();
  const queryClient = useQueryClient();

  const { id, product, plan } = useParams({ strict: false });
  const pathname = useLocation({ select: (location) => location.pathname });

  // Below tracks if on an ad or product level, context not carried through MultiMetricChart, so this should properly
  // decide whether to show custom annotations form or not
  const showCustomAnnotationForm = id || product || plan || supportedOrgLevelAnnotationPaths.includes(pathname);

  // Annotation CRUD Mutations
  const createAnnotation = useMutation({
    mutationFn: async (newAnnotation: {
      category?: string | number | null;
      marketplace: string;
      description: string;
      ts: string;
      product?: string | number | null;
      advertisement?: string | number | null;
    }) => {
      return await apiClient.createAnnotation(newAnnotation);
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [{ entity: "Annotations" }],
      });
      successToast({
        title: t(($) => $.annotations["Success"]),
        message: t(($) => $.annotations["Annotation created successfully"]),
      });
    },

    onError: (error) => {
      errorToast({ message: t(($) => $.annotations["Failed to create annotation"]), error });
    },
  });

  const updateAnnotation = useMutation({
    mutationFn: async (updatedAnnotation: {
      id: number;
      marketplace: string;
      description: string | null;
      ts: string;
    }) => {
      const { id, marketplace: annotationMarketplace, description, ts } = updatedAnnotation;
      const updatePayload: {
        marketplace: string;
        description: string | null;
        ts: string;
        category?: string | number | null;
      } = {
        marketplace: annotationMarketplace,
        description,
        ts,
      };

      if (category && category !== "all") {
        updatePayload.category = category;
      }

      return await apiClient.updateAnnotation(updatePayload, { params: { id } });
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [{ entity: "Annotations" }],
      });
      successToast({
        title: t(($) => $.annotations["Success"]),
        message: t(($) => $.annotations["Annotation updated successfully"]),
      });
    },

    onError: (error) => {
      errorToast({ message: t(($) => $.annotations["Failed to update annotation"]), error });
    },
  });

  const deleteAnnotation = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.deleteAnnotation(undefined, { params: { id } });
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [{ entity: "Annotations" }],
      });
      successToast({
        title: t(($) => $.annotations["Success"]),
        message: t(($) => $.annotations["Annotation deleted successfully"]),
      });
    },

    onError: (error) => {
      errorToast({ message: t(($) => $.annotations["Failed to delete annotation"]), error });
    },
  });

  const handleAddAnnotation = (formData: { annotation: string }) => {
    const { annotation: annotationText } = formData;

    const newAnnotationData: {
      category?: string | number | null;
      marketplace: string;
      description: string;
      ts: string;
      product?: string | number | null;
      advertisement?: string | number | null;
    } = {
      marketplace: marketplace,
      description: annotationText,
      ts: `${date}T00:00:00Z`,
    };

    if (category && category !== "all") {
      newAnnotationData["category"] = category;
    }

    if (selectedProduct) {
      newAnnotationData["product"] = selectedProduct as string | number;
    }

    if (advertisement) {
      newAnnotationData["advertisement"] = advertisement;
    }
    createAnnotation.mutate(newAnnotationData);
  };

  const handleEditAnnotation = (currentAnnotation: {
    id: number;
    marketplace: string;
    description: string | null;
    ts: string;
  }) => {
    updateAnnotation.mutate(currentAnnotation);
  };

  const handleRemoveAnnotation = (id: number) => {
    deleteAnnotation.mutate(id);
  };

  const clearForm = () => {
    setNewAnnotation({
      annotation: "",
      createdBy: user.user_full_name,
      date,
    });
  };

  // Find the annotation being deleted
  const annotationToDelete =
    deletingAnnotationId !== null ? data?.annotations?.find((a) => a.id === deletingAnnotationId) : null;

  return (
    <>
      {annotationToDelete ?
        <AnnotationDeleteConfirmation
          annotation={annotationToDelete}
          date={date}
          group={group}
          marketplace={marketplace}
          onCancel={() => {
            setDeletingAnnotationId(null);
          }}
          onConfirm={() => {
            handleRemoveAnnotation(deletingAnnotationId!);
            onClose();
          }}
          isDeleting={deleteAnnotation.isPending}
        />
      : <>
          <div className={labelHeaderStyle}>
            <Heading slot="title" level={6} className={labelTitleStyle}>
              {moment(date, ["MM-DD-YYYY", "MMM-YY"]).format("MMMM DD, YYYY") === "Invalid date" ?
                date
              : group === "month" ?
                fmtMonthYear.format(moment(date, ["YYYY-MM-DD"]).toDate())
              : fmtLong.format(moment(date, ["YYYY-MM-DD", "MMM-DD-YYYY", "MMM-DD"]).toDate())}
            </Heading>
            <Button variant="contentOnly" onPress={onClose} className={closeLabelButtonStyle}>
              <FaXmark />
            </Button>
          </div>
          <GridList
            aria-label={t(($) => $.annotations["Annotations"])}
            className={labelBodyStyle}
            // Hover events don't work if no selection mode
            selectionMode="single"
            items={data?.annotations?.slice(0, 50) || []}
          >
            {(annotation) => (
              <AnnotationCard key={annotation.id} id={annotation.id} textValue={getAnnotationTextValue(t, annotation)}>
                <AnnotationItem
                  annotation={annotation}
                  group={group}
                  marketplace={marketplace}
                  selectedProduct={selectedProduct}
                  setSelectedAnnotation={setSelectedAnnotation}
                  setDeletingAnnotationId={setDeletingAnnotationId}
                  deleteAnnotationIsPending={deleteAnnotation.isPending}
                />
              </AnnotationCard>
            )}
          </GridList>

          {group === "day" && showCustomAnnotationForm && (
            <VStack className={annotationFormStyle} gap="medium">
              <TextAreaField
                label={
                  <HStack justifyContent="space-between" alignItems="center" width="100%">
                    <span>{t(($) => $.annotations["Annotation Description"])}</span>
                    <Button
                      variant="contentOnly"
                      onPress={() => {
                        selectedAnnotation && setSelectedAnnotation(null);

                        setNewAnnotation({
                          annotation: "",
                          createdBy: user.user_full_name,
                          date,
                        });
                      }}
                      className={css({
                        padding: "xsmall",
                        color: "grey",
                        fontSize: "medium",
                        _hover: {
                          color: "darkgrey",
                        },
                      })}
                    >
                      <FaRotateLeft />
                    </Button>
                  </HStack>
                }
                name="annotation"
                value={(selectedAnnotation ? selectedAnnotation.description : newAnnotation.annotation) ?? ""}
                onChange={(value) => {
                  selectedAnnotation ?
                    setSelectedAnnotation({
                      ...selectedAnnotation,
                      description: value,
                    })
                  : setNewAnnotation({
                      ...newAnnotation,
                      annotation: value,
                    });
                }}
                rows={3}
                placeholder={t(($) => $.annotations["Enter annotation details..."])}
                className={css({ width: "100%" })}
              />

              <Button
                variant="primary"
                onPress={() => {
                  if (selectedAnnotation) {
                    handleEditAnnotation(selectedAnnotation);
                    setSelectedAnnotation(null);
                    clearForm();
                  } else if (newAnnotation.annotation.trim().length > 0) {
                    handleAddAnnotation(newAnnotation);
                    clearForm();
                  }
                }}
                isDisabled={createAnnotation.isPending || updateAnnotation.isPending}
                css={{
                  width: "100%",
                  fontSize: "small",
                  letterSpacing: "1px",
                }}
              >
                {createAnnotation.isPending ?
                  t(($) => $.annotations["Adding..."])
                : updateAnnotation.isPending ?
                  t(($) => $.annotations["Updating..."])
                : selectedAnnotation ?
                  t(($) => $.annotations["Update"])
                : t(($) => $.annotations["Add"])}
              </Button>
            </VStack>
          )}
        </>
      }
    </>
  );
};

/**
 * The annotations of one chart date, in a popover anchored to that date's annotation marker. It is portaled and kept
 * inside the viewport, so it never widens the page.
 */
export function AnnotationPopover({
  triggerRef,
  getTargetRect,
  date,
  onClose,
  ...panelProps
}: Omit<AnnotationPanelProps, "date"> & {
  triggerRef: RefObject<Element | null>;
  getTargetRect: (target: Element) => DOMRect | null;
  date: string | null;
}) {
  return (
    <Popover
      triggerRef={triggerRef}
      getTargetRect={getTargetRect}
      isOpen={date !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      className={popoverStyle}
    >
      <OverlayArrow color="white" />
      <Dialog className={dialogStyle}>
        {date !== null && <AnnotationPanel key={date} date={date} onClose={onClose} {...panelProps} />}
      </Dialog>
    </Popover>
  );
}
